/**
 * Observer aggregator — splits triggers by type and sets up
 * the appropriate observers. Returns a combined cleanup function.
 *
 * Triggers with a `url` pattern are filtered against the current page.
 * URL is also re-checked at event fire time for SPA navigation.
 */

import type { Trigger, EventMessage } from "../messages";
import { matchUrl } from "./url-match";
import { startMutationObserver, stopMutationObserver } from "./mutation";
import { setupDwellObservers } from "./dwell";
import { setupClickObservers } from "./click";
import { setupFormObservers } from "./form";
import { setupScrollObservers } from "./scroll";

/** Build a Set of trigger IDs that have a url filter, for re-checking at fire time. */
function buildUrlFilters(triggers: Trigger[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const t of triggers) {
    if (t.url) map.set(t.id, t.url);
  }
  return map;
}

export function setupObservers(
  triggers: Trigger[],
  onEvent: (msg: EventMessage) => void,
): () => void {
  const cleanups: (() => void)[] = [];

  // Filter triggers to those matching the current URL
  const currentUrl = window.location.href;
  const active = triggers.filter((t) => matchUrl(t.url, currentUrl));

  // Build URL filter map for re-checking at fire time (SPA navigation)
  const urlFilters = buildUrlFilters(active);
  const guardedOnEvent = (msg: EventMessage) => {
    const triggerId = "triggerId" in msg ? msg.triggerId : undefined;
    if (triggerId && urlFilters.has(triggerId)) {
      if (!matchUrl(urlFilters.get(triggerId), window.location.href)) return;
    }
    onEvent(msg);
  };

  const dwellTriggers = active.filter((t) => t.type === "dwell");
  const clickTriggers = active.filter((t) => t.type === "click");
  const formTriggers = active.filter((t) => t.type === "form");
  const scrollTriggers = active.filter((t) => t.type === "scroll_depth");

  // Start MutationObserver if any triggers need it
  if (dwellTriggers.length > 0 || formTriggers.length > 0) {
    startMutationObserver();
    cleanups.push(stopMutationObserver);
  }

  if (dwellTriggers.length > 0) {
    cleanups.push(setupDwellObservers(dwellTriggers, guardedOnEvent));
  }
  if (clickTriggers.length > 0) {
    cleanups.push(setupClickObservers(clickTriggers, guardedOnEvent));
  }
  if (formTriggers.length > 0) {
    cleanups.push(setupFormObservers(formTriggers, guardedOnEvent));
  }
  if (scrollTriggers.length > 0) {
    cleanups.push(setupScrollObservers(scrollTriggers, guardedOnEvent));
  }

  return () => {
    for (const fn of cleanups) fn();
  };
}
